import { useMemo, useState } from 'react';
import { Bell, MapPin, Repeat } from 'lucide-react';
import { AudiencePicker } from '../../sharing';
import {
  MAX_REMINDERS,
  REMINDER_PRESETS,
  WEEKDAYS,
  allDayRange,
  describeRRule,
  formatRRule,
  parseRRule,
  toDateKey,
  type CalendarEvent,
  type DocAudience,
  type NewEventPlacement,
  type Reminder,
} from '@awwyeah/shared';
import { hhmmToMinutes, minutesToHhmm } from '../timeParsing';
import { MemberAvatar } from './MemberBits';
import { QuickAddBox } from './QuickAddBox';
import { Sheet } from './Sheet';
import { TimeField } from './TimeField';
import type { MemberView } from '../members';

/**
 * Create / edit sheet, built for the 90% case: "Soccer, Thu 4pm, Ezry" (DESIGN-BRIEF §4.7).
 * Title, day, time and who are always visible; everything else has a sane default and
 * stays collapsed behind one row each.
 */

export type SaveScope = 'this' | 'all';

export interface EventDraft {
  title: string;
  dateKey: string;
  startTime: string;
  durationMinutes: number;
  allDay: boolean;
  ownerIds: string[];
  location: string;
  notes: string;
  reminders: Reminder[];
  recurrence: string | undefined;
  /**
   * WS-15. `family` = the shared calendar doc, everyone (the default and the 90% case).
   * `private` = written to `calendar:user:<me>`, which the server will not open for anyone
   * else. `members` = still the shared doc, filtered to `audienceIds` client-side — soft,
   * and the sheet says so in as many words rather than implying a lock.
   */
  audience: DocAudience;
  audienceIds: string[];
}

export interface EventFormSheetProps {
  open: boolean;
  /** The event being edited, or `null` for a new one. */
  event: CalendarEvent | null;
  /** Start of the occurrence being edited (repeating events). */
  occurrenceStart?: string;
  defaultDay: Date;
  defaultOwnerIds: string[];
  members: readonly MemberView[];
  /** Past titles, used for the one-tap suggestion row. */
  titleSuggestions: readonly string[];
  /** Which doc the event being edited currently lives in (WS-15). */
  placement?: 'family' | 'personal';
  /**
   * Where a *new* event starts out — my own setting (`useNewEventPlacement`). Only the
   * starting selection of "Who can see this"; the picker still changes it per event.
   */
  newEventPlacement?: NewEventPlacement;
  /** The signed-in member — excluded from the "some people" chips (always in my circle). */
  meId?: string | null;
  onCancel: () => void;
  onSave: (draft: EventDraft, scope: SaveScope) => void;
}

const DURATIONS = [
  { minutes: 30, label: '30m' },
  { minutes: 60, label: '1h' },
  { minutes: 90, label: '1h 30m' },
  { minutes: 180, label: '3h' },
];

function timeOf(date: Date): string {
  return `${`${date.getHours()}`.padStart(2, '0')}:${`${date.getMinutes()}`.padStart(2, '0')}`;
}

function draftFromEvent(
  event: CalendarEvent | null,
  occurrenceStart: string | undefined,
  defaultDay: Date,
  defaultOwnerIds: string[],
  placement: 'family' | 'personal',
  newEventPlacement: NewEventPlacement,
): EventDraft {
  if (!event) {
    const start = new Date(defaultDay);
    if (start.getHours() === 0 && start.getMinutes() === 0) start.setHours(16, 0, 0, 0);
    return {
      title: '',
      dateKey: toDateKey(start),
      startTime: timeOf(start),
      durationMinutes: 60,
      allDay: false,
      ownerIds: defaultOwnerIds,
      location: '',
      notes: '',
      reminders: [],
      recurrence: undefined,
      audience: newEventPlacement,
      audienceIds: [],
    };
  }
  const start = new Date(occurrenceStart ?? event.start);
  const durationMinutes = Math.max(
    15,
    Math.round((Date.parse(event.end) - Date.parse(event.start)) / 60_000),
  );
  return {
    title: event.title,
    dateKey: toDateKey(start),
    startTime: timeOf(start),
    durationMinutes: event.allDay ? 24 * 60 : durationMinutes,
    allDay: event.allDay,
    ownerIds: [...event.ownerIds],
    location: event.location ?? '',
    notes: event.notes ?? '',
    reminders: [...event.reminders],
    recurrence: event.recurrence,
    audience: placement === 'personal' ? 'private' : event.audience ? 'members' : 'family',
    audienceIds: [...(event.audience ?? [])],
  };
}

/** Repeat options are anchored on the chosen day, so "Weekly" means "weekly on Thu". */
function repeatOptions(dateKey: string): { value: string | undefined; label: string }[] {
  const weekday = WEEKDAYS[new Date(`${dateKey}T12:00:00`).getDay()] ?? 'MO';
  return [
    { value: undefined, label: 'Does not repeat' },
    { value: 'FREQ=DAILY', label: 'Daily' },
    { value: `FREQ=WEEKLY;BYDAY=${weekday}`, label: 'Weekly' },
    { value: `FREQ=WEEKLY;INTERVAL=2;BYDAY=${weekday}`, label: 'Every 2 weeks' },
    { value: 'FREQ=MONTHLY', label: 'Monthly' },
    { value: 'FREQ=YEARLY', label: 'Yearly' },
  ];
}

export function EventFormSheet({
  open,
  event,
  occurrenceStart,
  defaultDay,
  defaultOwnerIds,
  members,
  titleSuggestions,
  placement = 'family',
  newEventPlacement = 'family',
  meId = null,
  onCancel,
  onSave,
}: EventFormSheetProps): JSX.Element {
  const initial = useMemo(
    () =>
      draftFromEvent(
        event,
        occurrenceStart,
        defaultDay,
        defaultOwnerIds,
        placement,
        newEventPlacement,
      ),
    [event, occurrenceStart, defaultDay, defaultOwnerIds, placement, newEventPlacement],
  );
  const [draft, setDraft] = useState<EventDraft>(initial);
  const [signature, setSignature] = useState(`${event?.id ?? 'new'}:${occurrenceStart ?? ''}`);
  const [showExtras, setShowExtras] = useState(false);
  const [askScope, setAskScope] = useState(false);

  // Reset when the sheet is reopened for a different event (cheaper than a key remount).
  const nextSignature = `${event?.id ?? 'new'}:${occurrenceStart ?? ''}`;
  if (open && signature !== nextSignature) {
    setSignature(nextSignature);
    setDraft(initial);
    setShowExtras(false);
    setAskScope(false);
  }

  const patch = (next: Partial<EventDraft>): void =>
    setDraft((current) => ({ ...current, ...next }));
  const isRepeating = Boolean(event?.recurrence);
  const suggestions = draft.title.trim()
    ? titleSuggestions
        .filter(
          (title) =>
            title.toLowerCase().startsWith(draft.title.trim().toLowerCase()) &&
            title.toLowerCase() !== draft.title.trim().toLowerCase(),
        )
        .slice(0, 3)
    : [];

  const submit = (scope: SaveScope): void => {
    if (draft.title.trim().length === 0) return;
    setAskScope(false);
    onSave({ ...draft, title: draft.title.trim() }, scope);
  };

  const trySave = (): void => {
    if (isRepeating) setAskScope(true);
    else submit('all');
  };

  return (
    <>
      <Sheet
        open={open}
        onClose={onCancel}
        height="tall"
        label={event ? 'Edit event' : 'New event'}
        testId="event-form"
      >
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <button type="button" onClick={onCancel} className="h-11 px-1 text-sm text-fg-2">
            Cancel
          </button>
          <span className="text-sm font-semibold">{event ? 'Edit event' : 'New event'}</span>
          <button
            type="button"
            onClick={trySave}
            data-testid="event-save"
            disabled={draft.title.trim().length === 0}
            className="h-11 rounded-md px-3 text-sm font-semibold text-accent disabled:text-fg-3"
          >
            Save
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {!event && (
            // Each sentence fills a fresh form — saying it again must not keep the last
            // attempt's place or end time.
            <QuickAddBox members={members} onFill={(next) => setDraft({ ...initial, ...next })} />
          )}
          <Field label="What">
            <input
              type="text"
              value={draft.title}
              autoFocus
              data-testid="event-title-input"
              onChange={(e) => patch({ title: e.target.value })}
              placeholder="Soccer"
              className="h-12 w-full rounded-md border border-line bg-bg px-3 text-base text-fg outline-none focus:border-line-strong"
            />
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {suggestions.map((title) => (
                  <button
                    key={title}
                    type="button"
                    onClick={() => patch({ title })}
                    className="h-9 rounded-full border border-line bg-surface px-3 text-[13px] text-fg-2"
                  >
                    {title}
                  </button>
                ))}
              </div>
            )}
          </Field>

          <Field label="When">
            <input
              type="date"
              value={draft.dateKey}
              data-testid="event-date-input"
              onChange={(e) => patch({ dateKey: e.target.value })}
              className="h-12 w-full rounded-md border border-line bg-bg px-3 text-base text-fg"
            />
            {!draft.allDay && (
              <div className="flex items-center gap-2 pt-2">
                <TimeField
                  label="Start time"
                  testId="event-time-input"
                  value={draft.startTime}
                  onChange={(startTime) => {
                    // Keep the duration fixed and let the end time follow the new start,
                    // matching how dragging a calendar block by its start edge behaves.
                    patch({ startTime });
                  }}
                />
                <span className="text-sm text-fg-3">–</span>
                <TimeField
                  label="End time"
                  testId="event-end-time-input"
                  anchorMinutes={hhmmToMinutes(draft.startTime)}
                  value={minutesToHhmm(hhmmToMinutes(draft.startTime) + draft.durationMinutes)}
                  onChange={(endTime) => {
                    const start = hhmmToMinutes(draft.startTime);
                    const end = hhmmToMinutes(endTime);
                    const durationMinutes = Math.max(15, (end - start + 1440) % 1440 || 1440);
                    patch({ durationMinutes });
                  }}
                />
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-2">
              {DURATIONS.map((duration) => (
                <Chip
                  key={duration.minutes}
                  active={!draft.allDay && draft.durationMinutes === duration.minutes}
                  onClick={() => patch({ allDay: false, durationMinutes: duration.minutes })}
                >
                  {duration.label}
                </Chip>
              ))}
              <Chip active={draft.allDay} onClick={() => patch({ allDay: !draft.allDay })}>
                All day
              </Chip>
            </div>
          </Field>

          <Field label="Who">
            <div className="flex flex-wrap gap-2">
              {members.map((member) => {
                const active = draft.ownerIds.includes(member.id);
                return (
                  <button
                    key={member.id}
                    type="button"
                    data-testid={`event-owner-${member.id}`}
                    aria-pressed={active}
                    onClick={() =>
                      patch({
                        ownerIds: active
                          ? draft.ownerIds.filter((id) => id !== member.id)
                          : [...draft.ownerIds, member.id],
                      })
                    }
                    style={active ? { ...member.style.tint, ...member.style.text } : undefined}
                    className={`flex h-11 items-center gap-2 rounded-full pl-1 pr-3 text-sm font-semibold ${
                      active ? '' : 'border border-line bg-surface text-fg-2'
                    }`}
                  >
                    <MemberAvatar member={member} size="lg" />
                    {member.name}
                  </button>
                );
              })}
              {members.length === 0 && (
                <p className="text-sm text-fg-3">
                  No household members yet — they appear as everyone signs in.
                </p>
              )}
            </div>
          </Field>

          <Field label="Who can see this">
            <AudiencePicker
              value={{ audience: draft.audience, memberIds: draft.audienceIds }}
              onChange={(next) => patch({ audience: next.audience, audienceIds: next.memberIds })}
              members={members
                .filter((member) => member.id !== meId)
                .map((member) => ({ id: member.id, name: member.name, color: member.color }))}
              everyoneHint="On everyone's calendar"
              testId="event-audience"
            />
            {draft.audience === 'members' && (
              <p className="pt-2 text-[12px] text-fg-3" data-testid="event-audience-soft-note">
                Hidden from everyone else&apos;s calendar — but it still lives on the shared
                calendar, so treat it as tidiness, not a secret. &ldquo;Just me&rdquo; is the one
                that really locks.
              </p>
            )}
          </Field>

          {!showExtras ? (
            <button
              type="button"
              onClick={() => setShowExtras(true)}
              data-testid="event-more-options"
              className="mt-2 flex w-full flex-col gap-px overflow-hidden rounded-md border border-line"
            >
              <SummaryRow
                icon={<Bell className="h-4 w-4" />}
                label="Reminders"
                value={
                  draft.reminders.length === 0
                    ? 'None'
                    : `${draft.reminders.length} of ${MAX_REMINDERS}`
                }
              />
              <SummaryRow
                icon={<Repeat className="h-4 w-4" />}
                label="Repeat"
                value={describeRRule(parseRRule(draft.recurrence))}
              />
              <SummaryRow
                icon={<MapPin className="h-4 w-4" />}
                label="Where · Notes"
                value={draft.location || draft.notes ? 'Set' : 'Add'}
              />
            </button>
          ) : (
            <>
              <Field label={`Reminders · up to ${MAX_REMINDERS}`}>
                <div className="flex flex-wrap gap-2">
                  {REMINDER_PRESETS.map((preset) => {
                    const active = draft.reminders.some(
                      (reminder) => reminder.minutesBefore === preset.minutesBefore,
                    );
                    const full = draft.reminders.length >= MAX_REMINDERS;
                    return (
                      <Chip
                        key={preset.minutesBefore}
                        active={active}
                        disabled={!active && full}
                        onClick={() =>
                          patch({
                            reminders: active
                              ? draft.reminders.filter(
                                  (reminder) => reminder.minutesBefore !== preset.minutesBefore,
                                )
                              : [...draft.reminders, { minutesBefore: preset.minutesBefore }],
                          })
                        }
                      >
                        {preset.label}
                      </Chip>
                    );
                  })}
                </div>
              </Field>

              <Field label="Repeat">
                <select
                  value={draft.recurrence ?? ''}
                  data-testid="event-repeat-select"
                  onChange={(e) =>
                    patch({
                      recurrence:
                        e.target.value === ''
                          ? undefined
                          : formatRRule(
                              parseRRule(e.target.value) ?? { freq: 'WEEKLY', interval: 1 },
                            ),
                    })
                  }
                  className="h-12 w-full rounded-md border border-line bg-bg px-3 text-base text-fg"
                >
                  {repeatOptions(draft.dateKey).map((option) => (
                    <option key={option.label} value={option.value ?? ''}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Where">
                <input
                  type="text"
                  value={draft.location}
                  onChange={(e) => patch({ location: e.target.value })}
                  placeholder="Old Town Market"
                  className="h-12 w-full rounded-md border border-line bg-bg px-3 text-base text-fg"
                />
              </Field>

              <Field label="Notes">
                <textarea
                  value={draft.notes}
                  onChange={(e) => patch({ notes: e.target.value })}
                  rows={3}
                  className="w-full rounded-md border border-line bg-bg p-3 text-base text-fg"
                />
              </Field>
            </>
          )}
        </div>
      </Sheet>

      <Sheet
        open={askScope}
        onClose={() => setAskScope(false)}
        label="Save repeating event"
        testId="event-scope-sheet"
      >
        <div className="flex flex-col gap-3 p-4">
          <p className="font-display text-xl">This repeats</p>
          <p className="text-sm text-fg-2">Save the change to just this one, or all of them?</p>
          <button
            type="button"
            data-testid="scope-this"
            onClick={() => submit('this')}
            className="h-12 rounded-md border border-line-strong bg-surface text-sm font-semibold"
          >
            Just this one
          </button>
          <button
            type="button"
            data-testid="scope-all"
            onClick={() => submit('all')}
            className="h-12 rounded-md bg-inverse text-sm font-semibold text-inverse-fg"
          >
            All of them
          </button>
        </div>
      </Sheet>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="pb-5">
      <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-3">
        {label}
      </span>
      {children}
    </div>
  );
}

function Chip({
  active,
  disabled = false,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={active}
      className={`h-11 rounded-full px-4 text-sm font-semibold transition-colors duration-150 ease-app ${
        active ? 'bg-inverse text-inverse-fg' : 'border border-line bg-surface text-fg-2'
      } ${disabled ? 'opacity-40' : ''}`}
    >
      {children}
    </button>
  );
}

function SummaryRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}): JSX.Element {
  return (
    <span className="flex min-h-[48px] w-full items-center gap-3 bg-surface px-3 text-left">
      <span className="text-fg-3">{icon}</span>
      <span className="flex-1 text-sm font-semibold">{label}</span>
      <span className="text-sm text-fg-3">{value} →</span>
    </span>
  );
}

/** Turns a draft into the ISO start/end pair the schema stores. */
export function draftToTimes(draft: EventDraft): { start: string; end: string } {
  if (draft.allDay) return allDayRange(new Date(`${draft.dateKey}T00:00:00`));
  const start = new Date(`${draft.dateKey}T${draft.startTime}:00`);
  return {
    start: start.toISOString(),
    end: new Date(start.getTime() + draft.durationMinutes * 60_000).toISOString(),
  };
}
